import React, { useEffect } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { useQuery } from '@apollo/client';
import {
  Spinner,
  Flex,
  Text,
  Box,
  Badge,
  Button,
  Avatar,
  useDisclosure,
  useClipboard,
  Grid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorMode,
  Icon,
} from '@chakra-ui/react';
import moment from 'moment';
import useMedia from 'use-media';
import get from 'lodash/get';
import { Link } from '@reach/router';

import Layout from '../../components/Layout';
import Advertisements from './Tabs/Advertisements';
import Devices from './Tabs/Devices';
import DropPoint from './Tabs/DropPoint';
import History from './Tabs/History';
import Orders from './Tabs/Orders';
import LockConfirmModal from './modals/LockConfirmModal';
import UnlockConfirmModal from './modals/UnlockConfirmModal';
import CancelUser from './actions/CancelUser';
import ForceCancelUser from './actions/ForceCancelUser';
import DeleteUser from './actions/DeleteUser';
import SendEmailToUser from './actions/SendEmail';
import { EditUser } from './actions/EditUser';
import { UpdateDropPointAddress } from './actions/UpdateDropPointAddress';
import { EditEmail } from './actions/EditEmail';
import { AddNewEntry } from './actions/AddNewEntry';

import { GET_USER, GET_ACCOUNT, UserQuery } from './query';
import { ActivityCheck } from './actions/ActivityCheck';
import AccessPoint from './Tabs/AccessPoint';
import AccessPointVerificationTab from './Tabs/AccessPointVerification';
import { Check, ArrowSquareOut, Warning, X } from '@phosphor-icons/react';
import { UPSParcels } from './Tabs/UPSParcels';
import { AmazonParcels } from './Tabs/AmazonParcels';
import { PartnerStatistics } from './Tabs/PartnerStatistics';
import { SendUPSEmailButton } from './actions/SendUPSEmail';
import { CreateStripeAccount } from './actions/CreateStripeAccount';
import { CreateStripeCustomer } from './actions/CreateStripeCustomer';
import { ToggleDropPointStatus } from './actions/ToggleDropPointStatus';
import { DownloadDirectoryQrCode } from './actions/DownloadDirectoryQrCode';

interface UserDetailsProps extends RouteComponentProps {
  userId?: string;
}

const resolveAccessPointField = (packageStation: any, accessPoint: any, field: string) => {
  const stationValue = packageStation?.[field];
  const accessPointValue = accessPoint?.[field];

  if (stationValue !== null && typeof stationValue !== 'undefined') {
    return stationValue;
  }

  return accessPointValue ?? null;
};

type UserDetailsTabKey =
  | 'drop-point'
  | 'order-management'
  | 'access-point'
  | 'statistics'
  | 'access-point-verification'
  | 'ups-parcels'
  | 'amazon-parcels'
  | 'history'
  | 'devices'
  | 'advertisements';

export const getUserDetailsTabKeys = ({
  hasCompany,
  showAccessPointSupport,
  showAccessPointVerification,
  showUpsParcels,
  showAmazonParcels,
}: {
  hasCompany: boolean;
  showAccessPointSupport: boolean;
  showAccessPointVerification: boolean;
  showUpsParcels: boolean;
  showAmazonParcels: boolean;
}): UserDetailsTabKey[] => {
  const keys: UserDetailsTabKey[] = [];

  if (!hasCompany) {
    keys.push('drop-point', 'order-management');
  }
  if (showAccessPointSupport) {
    keys.push('access-point', 'statistics');
  }
  if (showAccessPointVerification) {
    keys.push('access-point-verification');
  }
  if (showUpsParcels) {
    keys.push('ups-parcels');
  }
  if (showAmazonParcels) {
    keys.push('amazon-parcels');
  }
  keys.push('history', 'devices');
  if (!hasCompany) {
    keys.push('advertisements');
  }

  return keys;
};

/** Tab icon for Access Point Verifizierung — based only on ID review outcome. */
export const getAccessPointVerificationTabIcon = (
  input: {
    identificationSuccess?: boolean | null;
    identificationSuccessTimestamp?: string | null;
    rejectionTimestamp?: string | null;
  } = {}
) => {
  if (input.rejectionTimestamp) return X;
  if (input.identificationSuccess || input.identificationSuccessTimestamp) return Check;
  return Warning;
};

const IdCopy = ({ id, label }) => {
  const { onCopy, hasCopied } = useClipboard(id);

  return (
    <Flex alignItems="center" mb="2">
      <Text mr="4" flex="1">
        <strong>{label}</strong> {id}
      </Text>
      <Button variant="link" size="xs" onClick={onCopy} colorScheme="brand" fontWeight="normal" letterSpacing="0.5px">
        {hasCopied ? 'Kopiert' : 'Kopieren'}
      </Button>
    </Flex>
  );
};

const UserDetails: React.FC<UserDetailsProps> = ({ userId, location }) => {
  const { colorMode } = useColorMode();
  const isMobile = useMedia({ maxWidth: '767px' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpen2, onOpen: onOpen2, onClose: onClose2 } = useDisclosure();
  const { isOpen: isOpenHistoryEntry, onOpen: onOpenHistoryEntry, onClose: onCloseHistoryEntry } = useDisclosure();
  const { loading, error, refetch, data } = useQuery<UserQuery>(GET_USER, {
    variables: {
      where: {
        id: userId,
      },
    },
  });
  const { data: accountData, refetch: accountRefetch } = useQuery(GET_ACCOUNT, {
    variables: {
      stationId: get(data, 'user.packageStations.0.id', ''),
    },
    skip: !get(data, 'user.packageStations.0.id', ''),
  });

  useEffect(() => {
    if (get(data, 'user.packageStations.0.id', false) && !accountData) {
      accountRefetch({
        stationId: get(data, 'user.packageStations.0.id', ''),
      });
    }
  }, [data, accountData, accountRefetch]);

  const handleLock = async () => {
    onOpen();
  };

  const handleUnLock = async () => {
    onOpen2();
  };

  // This is needed because the History Tab also opens the Modal
  const handleHistoryEntry = async () => {
    onOpenHistoryEntry();
  };

  if (loading) {
    return (
      <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
        <Spinner />
      </Flex>
    );
  }
  if (error) {
    return (
      <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
        Ein Fehler ist aufgetreten. Schreib Osamah an.
      </Flex>
    );
  }

  const {
    id,
    createdAt,
    photo,
    firstName,
    lastName,
    email,
    birthdate,
    packageStations,
    emailVerified,
    accountLocked,
    lockReason,
    inDefault,
    defaultAmount,
    address,
    stripeId,
    orders,
    acceptEmails,
    advertisements,
    companyRole,
    myCompany,
    devices,
  } = data.user;
  const packageStation = packageStations && packageStations.length > 0 ? packageStations[0] : ({} as any);
  const {
    id: dropPointId,
    isOnboarded,
    isActive,
    address: dropPointAddress,
    stripeAccountId,
    packages,
    lastActivityCheckSent,
    lastActivityCheckResponse,
    activityCheck,
    activityCheckSent,
    activityCheckToken,
    activityCheckAmount,
    accessPoint,
    hubCounter,
  } = packageStation;
  const accessPointWithStationData = accessPoint
    ? {
        ...accessPoint,
        submitTimestamp: resolveAccessPointField(packageStation, accessPoint, 'submitTimestamp'),
        idFront: resolveAccessPointField(packageStation, accessPoint, 'idFront'),
        idBack: resolveAccessPointField(packageStation, accessPoint, 'idBack'),
        idSelfie: resolveAccessPointField(packageStation, accessPoint, 'idSelfie'),
        identificationSuccess: resolveAccessPointField(packageStation, accessPoint, 'identificationSuccess'),
        identificationTries: resolveAccessPointField(packageStation, accessPoint, 'identificationTries'),
        identificationSuccessTimestamp: resolveAccessPointField(packageStation, accessPoint, 'identificationSuccessTimestamp'),
      }
    : null;
  const hasActiveAdvertisement = advertisements.find(({ isActive }) => isActive);
  const hasCompany = Boolean(myCompany?.id);
  const showAccessPointSupport = !hasCompany && Boolean(dropPointId) && Boolean(isOnboarded);
  const showAccessPointVerification = Boolean(accessPointWithStationData?.submitTimestamp);
  const showUpsParcels = Boolean(accessPointWithStationData?.accessPointID);
  const showAmazonParcels = showAccessPointSupport;
  const tabKeys = getUserDetailsTabKeys({
    hasCompany,
    showAccessPointSupport,
    showAccessPointVerification,
    showUpsParcels,
    showAmazonParcels,
  });

  return (
    <Layout>
      <Box mb="8">
        <Button variant="link" onClick={() => window.history.go(-1)} size={isMobile ? 'xs' : 'sm'} mr="4">
          {'< Zurück'}
        </Button>
        <Button
          colorScheme="brand"
          onClick={() => {
            refetch();
            accountRefetch();
          }}
          size={isMobile ? 'xs' : 'sm'}
        >
          User Neu laden
        </Button>
      </Box>
      <Flex mb="4">
        <Avatar src={photo ? photo.uri : null} mr={isMobile ? '3' : '8'} size={isMobile ? 'md' : '2xl'} />
        <Box>
          <Flex mb="1" flexWrap="wrap">
            {accountLocked && lockReason === 'ACCOUNT_DISABLED' ? (
              <Badge colorScheme="red" mr="2" mb="1">
                Account gekündigt
              </Badge>
            ) : (
              <>
                {myCompany?.id ? (
                  <Badge
                    as={Link}
                    colorScheme="blue"
                    mr="2"
                    mb="1"
                    // @ts-ignore
                    to={`/companies/${myCompany.id}`}
                  >
                    {myCompany.name}: {companyRole}
                  </Badge>
                ) : null}
                {accountLocked ? (
                  <Badge colorScheme="red" mr="2" mb="1">
                    Account gesperrt
                  </Badge>
                ) : null}
                {inDefault ? (
                  <Badge colorScheme="red" mr="2" mb="1">
                    Zahlungsrückstand: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(defaultAmount / 100)}
                  </Badge>
                ) : null}
                {!myCompany?.id && !isOnboarded ? (
                  <Badge colorScheme="yellow" mr="2" mb="1">
                    DropPoint nicht erstellt
                  </Badge>
                ) : null}
                {!myCompany?.id && isOnboarded ? (
                  <Badge variant="solid" mr="2" colorScheme={isActive ? 'green' : 'red'} mb="1">
                    {isActive ? 'DropPoint aktiv' : 'DropPoint inaktiv'}
                  </Badge>
                ) : null}
                <Badge colorScheme={acceptEmails ? 'green' : 'orange'} mb="1" mr="2">
                  {acceptEmails ? 'Werbung gestattet' : 'Werbung unerwünscht'}
                </Badge>
                {advertisements.length > 0 ? (
                  <Badge variant="solid" colorScheme={hasActiveAdvertisement ? 'green' : 'yellow'} mb="1">
                    {hasActiveAdvertisement ? 'Ad online' : 'Ad offline'}
                  </Badge>
                ) : null}
                <Badge colorScheme="orange" mb="1">
                  {emailVerified ? 'Email verifiziert' : 'Email nicht verifiziert'}
                </Badge>
              </>
            )}
          </Flex>
          <Flex>
            <Text fontSize="3xl" fontFamily="heading">
              {firstName} {lastName}
            </Text>
          </Flex>
          <Text>{email}</Text>
          <Text fontSize="sm">Geboren am {moment(birthdate).format('DD.MM.YYYY')}</Text>
          <Text fontSize="sm" mb="4" color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>
            Registriert seit {moment(createdAt).format('DD.MM.YYYY, HH:mm')}
          </Text>
          {!myCompany?.id && isOnboarded ? (
            <Box mb="4">
              <Text fontSize="sm" color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>
                {activityCheckSent && activityCheck
                  ? 'Aktivitätsanfrage beantwortet'
                  : activityCheckSent && !activityCheck
                    ? 'Aktivitätsanfrage gesendet, aber noch nicht beantwortet'
                    : 'Aktivitätsanfrage noch nicht gesendet.'}
              </Text>
              <Text fontSize="sm" color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>
                Letzte Aktivitätsanfrage gesendet:{' '}
                {lastActivityCheckSent ? moment(lastActivityCheckSent).format('DD.MM.YYYY, HH:mm') : 'Noch nicht gesendet'}
              </Text>
              <Text fontSize="sm" color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>
                Letzte Rückmeldung zur Aktivität:{' '}
                {lastActivityCheckResponse ? moment(lastActivityCheckResponse).format('DD.MM.YYYY, HH:mm') : 'Noch nicht beantwortet'}
              </Text>
              <Text fontSize="sm" color={colorMode === 'light' ? 'gray.700' : 'gray.300'}>
                {activityCheckAmount || '0'} Aktivitätsanfragen gesendet
              </Text>
              {/*
          for testing purposes */}
              {process.env.NODE_ENV === 'development' ? (
                <Text fontSize="sm" mb="2" color="red.500">
                  activityCheckToken
                  <br />
                  {activityCheckToken}
                </Text>
              ) : null}
            </Box>
          ) : null}

          <Flex alignItems="center" flexWrap="wrap" gap="2" mb="2">
            {accountLocked && lockReason === 'ACCOUNT_DISABLED' ? null : (
              <>
                {isActive ? <ActivityCheck refetch={refetch} where={{ user: { id: data?.user.id } }} user={data?.user} /> : null}

                <SendUPSEmailButton refetch={refetch} where={{ id: data?.user.id }} totalUsers={1} isSmall />

                {!myCompany?.id && isOnboarded ? (
                  <ToggleDropPointStatus stationId={dropPointId} isActive={isActive} refetch={refetch} />
                ) : null}

                {!myCompany?.id && isOnboarded ? <DownloadDirectoryQrCode dropPointId={dropPointId} /> : null}

                <EditUser refetch={refetch} user={data?.user} />

                <EditEmail refetch={refetch} userId={data?.user.id} email={email} />
                <AddNewEntry
                  refetch={refetch}
                  userId={data?.user.id}
                  onOpen={onOpenHistoryEntry}
                  onClose={onCloseHistoryEntry}
                  isOpen={isOpenHistoryEntry}
                />
                <SendEmailToUser refetch={refetch} user={data?.user} />
                {!emailVerified ? (
                  <Button variant="outline" colorScheme="brand" size="xs">
                    Verifizierungsemail senden
                  </Button>
                ) : null}
                {accountLocked ? (
                  <Button variant="outline" colorScheme="red" size="xs" onClick={handleUnLock}>
                    User entsperren
                  </Button>
                ) : (
                  <Button variant="outline" colorScheme="red" size="xs" onClick={handleLock}>
                    User sperren
                  </Button>
                )}
                {myCompany?.id ? null : <CancelUser refetch={refetch} user={data?.user} />}
                {myCompany?.id ? null : <ForceCancelUser refetch={refetch} user={data?.user} />}
                {/* {orders.length > 0 || packages?.length > 0 ? null : <DeleteUser user={data?.user} />} */}
                <Button
                  as="a"
                  href={`https://dashboard.stripe.com/acct_1FqI2OGIyverIoOC/customers/${stripeId}`}
                  target="_blank"
                  rightIcon={<ArrowSquareOut />}
                  variant="outline"
                  colorScheme="blue"
                  size="xs"
                >
                  Booker PayMethods einsehen
                </Button>
                <Button
                  as="a"
                  href={`https://dashboard.stripe.com/acct_1FqI2OGIyverIoOC/connect/accounts/${stripeAccountId}/activity`}
                  rightIcon={<ArrowSquareOut />}
                  target="_blank"
                  variant="outline"
                  colorScheme="blue"
                  size="xs"
                >
                  DropPoint Geldpool einsehen
                </Button>
                <CreateStripeCustomer userId={userId} refetch={refetch} />
                <CreateStripeAccount packageStationId={dropPointId} refetch={refetch} />
              </>
            )}
          </Flex>
        </Box>
      </Flex>

      <IdCopy label="Besteller-ID:" id={id} />
      <IdCopy label="DropPoint-ID:" id={dropPointId} />
      <Grid templateColumns={['1fr', '1fr 1fr']} gridGap="8" my="8">
        {address ? (
          <Box>
            <Text fontWeight="bold">Rechnungsadresse:</Text>
            <Text>
              {address.street} {address.houseNumber}
            </Text>
            <Text>
              {address.postalCode} {address.city}
            </Text>
          </Box>
        ) : null}
        {dropPointAddress || dropPointId ? (
          <Box>
            <Text fontWeight="bold">DropPoint Adresse:</Text>
            {dropPointAddress ? (
              <>
                <Text>
                  {dropPointAddress.street} {dropPointAddress.houseNumber}
                </Text>
                <Text>
                  {dropPointAddress.postalCode} {dropPointAddress.city}
                </Text>
                <Text>{get(accountData, 'retrieveAccount.phone')}</Text>
              </>
            ) : (
              <Text color="gray.500">Keine DropPoint-Adresse hinterlegt</Text>
            )}
            <Box mt="2">
              <UpdateDropPointAddress dropPointId={dropPointId} address={dropPointAddress} refetch={refetch} />
            </Box>
          </Box>
        ) : null}
      </Grid>
      <Tabs variant="line" colorScheme="brand">
        <Box overflowX="auto">
          <TabList width="max-content" minW="full">
            {tabKeys.map((key) => {
              if (key === 'drop-point') {
                return (
                  <Tab key={key} fontSize="sm">
                    DropPoint
                  </Tab>
                );
              }
              if (key === 'order-management') {
                return (
                  <Tab key={key} fontSize="sm">
                    Besteller Verwaltung
                  </Tab>
                );
              }
              if (key === 'access-point') {
                return (
                  <Tab key={key} fontSize="sm">
                    Partner Programm
                  </Tab>
                );
              }
              if (key === 'statistics') {
                return (
                  <Tab key={key} fontSize="sm">
                    Statistik
                  </Tab>
                );
              }
              if (key === 'access-point-verification') {
                return (
                  <Tab key={key} fontSize="sm">
                    <Icon
                      as={getAccessPointVerificationTabIcon({
                        identificationSuccess: accessPointWithStationData?.identificationSuccess,
                        identificationSuccessTimestamp: accessPointWithStationData?.identificationSuccessTimestamp,
                        rejectionTimestamp: accessPointWithStationData?.rejectionTimestamp,
                      })}
                      mr="1"
                    />
                    Access Point Verifizierung
                  </Tab>
                );
              }
              if (key === 'ups-parcels') {
                return (
                  <Tab key={key} fontSize="sm">
                    UPS Pakete
                  </Tab>
                );
              }
              if (key === 'amazon-parcels') {
                return (
                  <Tab key={key} fontSize="sm">
                    Amazon Pakete
                  </Tab>
                );
              }
              if (key === 'history') {
                return (
                  <Tab key={key} fontSize="sm">
                    History
                  </Tab>
                );
              }
              if (key === 'devices') {
                return (
                  <Tab key={key} fontSize="sm">
                    Geräte
                  </Tab>
                );
              }

              return (
                <Tab key={key} fontSize="sm">
                  Werbeverwaltung
                </Tab>
              );
            })}
          </TabList>
        </Box>
        <TabPanels>
          {tabKeys.map((key) => {
            if (key === 'drop-point') {
              return (
                <TabPanel key={key} p="0">
                  <DropPoint dropPointId={dropPointId} />
                </TabPanel>
              );
            }
            if (key === 'order-management') {
              return (
                <TabPanel key={key} p="0">
                  <Orders userId={userId} location={location} />
                </TabPanel>
              );
            }
            if (key === 'access-point') {
              return (
                <TabPanel key={key} p="0">
                  <AccessPoint
                    accessPoint={accessPointWithStationData}
                    hubCounter={hubCounter}
                    dropPointId={dropPointId}
                    refetch={refetch}
                  />
                </TabPanel>
              );
            }
            if (key === 'statistics') {
              return (
                <TabPanel key={key} p="0">
                  <PartnerStatistics userId={userId} />
                </TabPanel>
              );
            }
            if (key === 'access-point-verification') {
              return (
                <TabPanel key={key} p="0">
                  <AccessPointVerificationTab {...accessPointWithStationData} dropPointId={dropPointId} refetch={refetch} />
                </TabPanel>
              );
            }
            if (key === 'ups-parcels') {
              return (
                <TabPanel key={key} p="0">
                  <UPSParcels {...accessPointWithStationData} refetch={refetch} />
                </TabPanel>
              );
            }
            if (key === 'amazon-parcels') {
              return (
                <TabPanel key={key} p="0">
                  <AmazonParcels hubCounter={hubCounter} />
                </TabPanel>
              );
            }
            if (key === 'history') {
              return (
                <TabPanel key={key} p="0">
                  <History
                    userId={userId}
                    accessPointId={accessPoint?.id}
                    dropPointId={dropPointId}
                    handleHistoryEntry={handleHistoryEntry}
                  />
                </TabPanel>
              );
            }
            if (key === 'devices') {
              return (
                <TabPanel key={key} p="0">
                  <Devices devices={devices || []} />
                </TabPanel>
              );
            }

            return (
              <TabPanel key={key} p="0">
                <Advertisements userId={userId} />
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
      <LockConfirmModal isOpen={isOpen} onClose={onClose} refetch={refetch} userId={userId} />
      <UnlockConfirmModal isOpen={isOpen2} onClose={onClose2} refetch={refetch} userId={userId} />
    </Layout>
  );
};

export default UserDetails;
